import React, { useEffect, useMemo, useState } from 'react';
import { downloadJSON, downloadCSV } from 'utils/download';
import {
  subscribe,
  stats as getStats,
  listMessages,
  searchMessages,
  upsertMessage,
  removeMessage,
  listAlerts,
  upsertAlert,
  removeAlert,
  exportJSON,
  importJSON,
  exportCSV,
  listChannels,
  addChannel,
  removeChannel,
  simulateMessages,
} from 'services/signal/slackMonitoringService';

export default function SupplierSlackMonitoring(){
  const [state, setState] = useState({ messages: [], alerts: [] });
  const [q, setQ] = useState('');
  const [channel, setChannel] = useState('all');
  const [status, setStatus] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [type, setType] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [newMsg, setNewMsg] = useState({ channel:'#general', text:'', severity:'info', type:'post', status:'success' });
  const [newAlert, setNewAlert] = useState({ name:'', channel:'#general', match:'', severity:'info', enabled:true });
  const [newChannel, setNewChannel] = useState('');
  const [simCount, setSimCount] = useState(5);
  const [simErrorRate, setSimErrorRate] = useState(0.2);

  useEffect(()=> subscribe(setState), []);
  const items = useMemo(()=> searchMessages({ q, channel, status, severity, type, from: from||null, to: to||null }), [state, q, channel, status, severity, type, from, to]);
  const kpis = useMemo(()=> getStats(), [state, q, channel, status, severity, type, from, to]);

  const onExportJSON = ()=> downloadJSON(exportJSON(), 'slack-monitoring.json');
  const onExportCSV = ()=> downloadCSV(exportCSV(), 'slack-messages.csv');
  const onImport = async ()=>{
    const input = document.createElement('input'); input.type='file'; input.accept='.json,application/json';
    input.onchange = async ()=>{ const f=input.files[0]; if(!f) return; const t=await f.text(); try{ importJSON(JSON.parse(t)); }catch{ alert('Invalid JSON'); } };
    input.click();
  };

  const createMessage = (e)=>{ e.preventDefault(); if(!newMsg.text.trim()) return; upsertMessage(newMsg); setNewMsg({ ...newMsg, text:'' }); };
  const createAlert = (e)=>{ e.preventDefault(); if(!newAlert.name.trim()||!newAlert.match.trim()) return; upsertAlert(newAlert); setNewAlert({ name:'', channel:'#general', match:'', severity:'info', enabled:true }); };

  const channels = useMemo(()=> listChannels(), [state]);

  return (
    <div className="page-container">
      <div className="page-header d-flex justify-content-between align-items-end flex-wrap gap-2">
        <div>
          <h1>Slack Monitoring</h1>
          <p className="lead">Supplier ecosystem: monitor Slack integration activity and alerts</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-outline-secondary" onClick={onImport}><i className="bi bi-upload me-1"/>Import</button>
          <button className="btn btn-outline-primary" onClick={onExportJSON}><i className="bi bi-download me-1"/>Export JSON</button>
          <button className="btn btn-outline-primary" onClick={onExportCSV}><i className="bi bi-filetype-csv me-1"/>Export CSV</button>
        </div>
      </div>

      <div className="content-section">
        <div className="row mb-3">
          <div className="col-md-3"><div className="card h-100"><div className="card-body text-center"><div className="small text-muted">Total Events</div><div className="metric-value">{kpis.total||0}</div></div></div></div>
          <div className="col-md-3"><div className="card h-100"><div className="card-body text-center"><div className="small text-muted">Success</div><div className="metric-value">{kpis.success||0}</div></div></div></div>
          <div className="col-md-3"><div className="card h-100"><div className="card-body text-center"><div className="small text-muted">Errors</div><div className="metric-value">{kpis.errors||0}</div></div></div></div>
          <div className="col-md-3"><div className="card h-100"><div className="card-body text-center"><div className="small text-muted">Channels</div><div className="metric-value">{kpis.channels||0}</div></div></div></div>
        </div>

        <div className="card mb-3"><div className="card-body d-flex flex-wrap gap-2">
          <input className="form-control" placeholder="Search text, channel, severity" value={q} onChange={(e)=> setQ(e.target.value)} />
          <select className="form-select" value={channel} onChange={(e)=> setChannel(e.target.value)} style={{ maxWidth:200 }}>
            <option value="all">All Channels</option>
            {channels.map(c=> <option key={c} value={c}>{c}</option>)}
          </select>
          <select className="form-select" value={status} onChange={(e)=> setStatus(e.target.value)} style={{ maxWidth:160 }}>
            <option value="all">Any Status</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
          </select>
          <select className="form-select" value={severity} onChange={(e)=> setSeverity(e.target.value)} style={{ maxWidth:160 }}>
            <option value="all">Any Severity</option>
            <option value="info">Info</option>
            <option value="high">High</option>
          </select>
          <select className="form-select" value={type} onChange={(e)=> setType(e.target.value)} style={{ maxWidth:160 }}>
            <option value="all">Any Type</option>
            <option value="post">Post</option>
            <option value="event">Event</option>
          </select>
          <input type="date" className="form-control" value={from} onChange={(e)=> setFrom(e.target.value)} style={{ maxWidth:180 }} />
          <input type="date" className="form-control" value={to} onChange={(e)=> setTo(e.target.value)} style={{ maxWidth:180 }} />
        </div></div>

        <div className="row">
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead><tr>
                      <th style={{width:160}}>Time</th>
                      <th>Channel</th>
                      <th>Text</th>
                      <th>Severity</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr></thead>
                    <tbody>
                      {items.map(m=> (
                        <tr key={m.id}>
                          <td>{new Date(m.ts).toLocaleString()}</td>
                          <td>{m.channel}</td>
                          <td>{m.text}</td>
                          <td><span className={`badge bg-${m.severity==='high'?'danger':'info'}`}>{m.severity}</span></td>
                          <td><span className={`badge bg-${m.status==='success'?'success':'danger'}`}>{m.status}</span></td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-danger" onClick={()=> removeMessage(m.id)}><i className="bi bi-trash"/></button>
                          </td>
                        </tr>
                      ))}
                      {items.length===0 && (<tr><td colSpan={6} className="text-center text-muted py-4">No events</td></tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                <h4 className="mb-3">Post Test Message</h4>
                <form className="row g-2" onSubmit={createMessage}>
                  <div className="col-md-3">
                    <select className="form-select" value={newMsg.channel} onChange={(e)=> setNewMsg({ ...newMsg, channel:e.target.value })}>
                      <option>#general</option>
                      <option>#security-updates</option>
                      <option>#compliance-alerts</option>
                    </select>
                  </div>
                  <div className="col-md-6"><input className="form-control" placeholder="Message text" value={newMsg.text} onChange={(e)=> setNewMsg({ ...newMsg, text:e.target.value })} /></div>
                  <div className="col-md-2">
                    <select className="form-select" value={newMsg.severity} onChange={(e)=> setNewMsg({ ...newMsg, severity:e.target.value })}>
                      <option>info</option>
                      <option>high</option>
                    </select>
                  </div>
                  <div className="col-md-1 d-grid"><button className="btn btn-primary" type="submit"><i className="bi bi-send"/></button></div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card">
              <div className="card-body">
                <h4 className="mb-3">Alert Rules</h4>
                <div className="table-responsive">
                  <table className="table align-middle">
                    <thead><tr>
                      <th>Name</th>
                      <th>Channel</th>
                      <th>Match</th>
                      <th>Severity</th>
                      <th className="text-end">Actions</th>
                    </tr></thead>
                    <tbody>
                      {listAlerts().map(a=> (
                        <tr key={a.id}>
                          <td>{a.name}</td>
                          <td>{a.channel}</td>
                          <td><code>{a.match}</code></td>
                          <td><span className={`badge bg-${a.severity==='high'?'danger':'info'}`}>{a.severity}</span></td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-danger" onClick={()=> removeAlert(a.id)}><i className="bi bi-trash"/></button>
                          </td>
                        </tr>
                      ))}
                      {listAlerts().length===0 && (<tr><td colSpan={5} className="text-center text-muted py-3">No alert rules</td></tr>)}
                    </tbody>
                  </table>
                </div>

                <form className="row g-2" onSubmit={createAlert}>
                  <div className="col-12"><input className="form-control" placeholder="Rule name" value={newAlert.name} onChange={(e)=> setNewAlert({ ...newAlert, name:e.target.value })} /></div>
                  <div className="col-6"><input className="form-control" placeholder="#channel" value={newAlert.channel} onChange={(e)=> setNewAlert({ ...newAlert, channel:e.target.value })} /></div>
                  <div className="col-6"><input className="form-control" placeholder="Match regex" value={newAlert.match} onChange={(e)=> setNewAlert({ ...newAlert, match:e.target.value })} /></div>
                  <div className="col-6">
                    <select className="form-select" value={newAlert.severity} onChange={(e)=> setNewAlert({ ...newAlert, severity:e.target.value })}>
                      <option>info</option>
                      <option>high</option>
                    </select>
                  </div>
                  <div className="col-6 d-grid"><button className="btn btn-outline-primary" type="submit"><i className="bi bi-plus-lg me-1"/>Add Rule</button></div>
                </form>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                <h4 className="mb-3">Channels</h4>
                <div className="mb-2">
                  {channels.length===0 && <div className="text-muted">No channels</div>}
                  {channels.map(c=> (
                    <div key={c} className="d-flex align-items-center justify-content-between mb-1">
                      <span>{c}</span>
                      <button className="btn btn-sm btn-outline-danger" onClick={()=> removeChannel(c)}><i className="bi bi-trash"/></button>
                    </div>
                  ))}
                </div>
                <div className="d-flex gap-2">
                  <input className="form-control" placeholder="#new-channel" value={newChannel} onChange={(e)=> setNewChannel(e.target.value)} />
                  <button className="btn btn-outline-primary" onClick={()=> { if(newChannel.trim()) { addChannel(newChannel.trim()); setNewChannel(''); } }}>Add</button>
                </div>
              </div>
            </div>

            <div className="card mt-3">
              <div className="card-body">
                <h4 className="mb-3">Simulate Inbound Events</h4>
                <div className="row g-2 align-items-end">
                  <div className="col-4">
                    <label className="form-label">Count</label>
                    <input type="number" className="form-control" value={simCount} onChange={(e)=> setSimCount(Number(e.target.value||0))} />
                  </div>
                  <div className="col-5">
                    <label className="form-label">Error Rate</label>
                    <input type="number" step="0.05" min="0" max="1" className="form-control" value={simErrorRate} onChange={(e)=> setSimErrorRate(Number(e.target.value||0))} />
                  </div>
                  <div className="col-3 d-grid">
                    <button className="btn btn-outline-secondary" onClick={()=> simulateMessages(simCount, simErrorRate)}>Run</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
